import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { playTabSwitch } from '../utils/SoundManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getRelativeTime(dateStr) {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffMin < 1) return 'الآن';
    if (diffMin === 1) return 'منذ دقيقة';
    if (diffMin === 2) return 'منذ دقيقتين';
    if (diffMin <= 10) return `منذ ${diffMin} دقائق`;
    if (diffMin < 60) return `منذ ${diffMin} دقيقة`;
    if (diffHour === 1) return 'منذ ساعة';
    if (diffHour === 2) return 'منذ ساعتين';
    if (diffHour <= 10) return `منذ ${diffHour} ساعات`;
    if (diffHour < 24) return `منذ ${diffHour} ساعة`;
    if (diffDay === 1) return 'منذ يوم';
    if (diffDay === 2) return 'منذ يومين';
    if (diffDay <= 10) return `منذ ${diffDay} أيام`;
    return `منذ ${diffDay} يوم`;
  } catch {
    return '';
  }
}

function StatsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [viewers, setViewers] = useState([]);
  const [likers, setLikers] = useState([]);
  // قراءة tab من URL: ?tab=views | ?tab=likes
  const initialTab = (() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('tab');
    if (t === 'views') return 'viewers';
    if (t === 'likes') return 'likers';
    return 'overview';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);

  const getHeaders = () => {
    const uid = localStorage.getItem('userId');
    return uid ? { 'X-User-Id': uid } : {};
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, viewersRes, likersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/stats/my`, { withCredentials: true, headers: getHeaders() }),
        axios.get(`${BACKEND_URL}/api/stats/viewers`, { withCredentials: true, headers: getHeaders() }),
        axios.get(`${BACKEND_URL}/api/stats/likers`, { withCredentials: true, headers: getHeaders() })
      ]);
      setStats(statsRes.data);
      setViewers(viewersRes.data.viewers || []);
      setLikers(likersRes.data.likers || []);
      setIsPremium(viewersRes.data.is_premium);
    } catch (err) {
      console.error('Error fetching stats:', err);
      if (err.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>جاري التحميل...</p>
      </div>
    );
  }

  const statCards = [
    { 
      icon: '👁️', 
      label: 'زيارات ملفك', 
      value: stats?.views_count || 0,
      color: '#4facfe',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    },
    { 
      icon: '❤️', 
      label: 'إعجابات', 
      value: stats?.likes_count || 0,
      color: '#f093fb',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    { 
      icon: '💌', 
      label: 'طلبات واردة', 
      value: stats?.requests_received || 0,
      color: '#667eea',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    { 
      icon: '💬', 
      label: 'محادثات نشطة', 
      value: stats?.active_conversations || 0,
      color: '#00D9A5',
      gradient: 'linear-gradient(135deg, #00D9A5 0%, #00B388 100%)'
    }
  ];

  const renderUserCard = (person, timeField, isPremiumRequired) => {
    const isBlurred = person.is_blurred && !isPremium;
    
    return (
      <div 
        key={person.user_id}
        data-testid={`user-card-${person.user_id}`}
        onClick={() => !isBlurred && navigate(`/profile/${person.user_id}`)}
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '14px',
          padding: '14px 18px',
          borderBottom: '1px solid var(--border-color)',
          cursor: isBlurred ? 'default' : 'pointer',
          transition: 'background 0.2s',
          background: 'var(--card-bg)'
        }}
      >
        <div className="profile-avatar" style={{ 
          width: '48px', 
          height: '48px', 
          fontSize: '18px',
          flexShrink: 0,
          filter: isBlurred ? 'blur(6px)' : 'none'
        }}>
          {person.name?.charAt(0) || '؟'}
        </div>
        
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4 style={{ 
            margin: '0 0 4px', 
            fontSize: '15px', 
            color: 'var(--text-dark)',
            filter: isBlurred ? 'blur(4px)' : 'none'
          }}>
            {person.name || 'مستخدم'}
          </h4>
          <p style={{ 
            margin: 0, 
            fontSize: '13px', 
            color: 'var(--text-light)',
            filter: isBlurred ? 'blur(3px)' : 'none'
          }}>
            {person.age ? `${person.age} سنة` : ''}
            {person.city ? ` · ${person.city}` : ''}
            {person.job ? ` · ${person.job}` : ''}
          </p>
        </div>
        
        <div style={{ textAlign: 'left', flexShrink: 0 }}>
          <span style={{ fontSize: '12px', color: 'var(--text-light)' }}>
            {getRelativeTime(person[timeField])}
          </span>
          {person.view_count > 1 && (
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'var(--primary)' }}>
              {person.view_count} زيارة
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--light-bg)' }}>
      {/* Header */}
      <div className="header">
        <div className="header-content">
          <h1 className="logo" onClick={() => navigate('/dashboard')} style={{cursor: 'pointer'}}>وِداد</h1>
          <div className="nav-menu">
            <button className="nav-link" onClick={() => navigate('/dashboard')}>الرئيسية</button>
            <button className="nav-link" onClick={() => navigate('/search')}>البحث</button>
            <button className="nav-link" onClick={() => navigate('/messages')}>الرسائل</button>
            <button className="nav-link" style={{background: 'var(--light-bg)', color: 'var(--primary)'}}>الإحصائيات</button>
          </div>
        </div>
      </div>

      <div className="container" style={{ paddingTop: '24px', paddingBottom: '80px', maxWidth: '900px' }}>
        
        {/* Stats Overview Cards */}
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '28px' }}>
          {statCards.map((card) => (
            <div 
              key={card.label}
              data-testid={`stat-card-${card.label}`}
              className="card"
              style={{ 
                textAlign: 'center', 
                padding: '24px 16px',
                borderRadius: '16px',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div style={{
                position: 'absolute',
                top: 0, left: 0, right: 0,
                height: '4px',
                background: card.gradient
              }} />
              <div style={{ fontSize: '36px', marginBottom: '8px' }}>{card.icon}</div>
              <div style={{ 
                fontSize: '36px', 
                fontWeight: '800', 
                background: card.gradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '4px'
              }}>
                {card.value}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--text-light)', fontWeight: '600' }}>
                {card.label}
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <button
            data-testid="tab-viewers"
            className={`btn ${activeTab === 'viewers' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => { playTabSwitch(); setActiveTab('viewers'); }}
          >
            👁️ من شاهد ملفك ({viewers.length})
          </button>
          <button
            data-testid="tab-likers"
            className={`btn ${activeTab === 'likers' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => { playTabSwitch(); setActiveTab('likers'); }}
          >
            ❤️ المعجبون ({likers.length})
          </button>
        </div>

        {/* Premium Blur Notice */}
        {!isPremium && (viewers.length > 0 || likers.length > 0) && (
          <div 
            data-testid="premium-notice"
            className="card" 
            style={{ 
              background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1), rgba(255, 152, 0, 0.1))',
              border: '1px solid rgba(255, 193, 7, 0.3)',
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'center',
              borderRadius: '12px'
            }}
          >
            <p style={{ margin: '0 0 8px', fontSize: '15px', color: 'var(--text-dark)', fontWeight: '600' }}>
              👑 اشترك في العضوية المميزة لمعرفة من شاهد ملفك وأعجب بك
            </p>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/pricing')}
              style={{ padding: '10px 24px', fontSize: '14px' }}
            >
              عرض الباقات
            </button>
          </div>
        )}

        {/* Viewers List */}
        {activeTab === 'viewers' && (
          <div className="card" style={{ padding: 0, overflow: 'hidden', borderRadius: '16px' }}>
            {viewers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '50px 20px' }}>
                <div style={{ fontSize: '56px', marginBottom: '12px' }}>👁️</div>
                <h3 style={{ color: 'var(--text-dark)', marginBottom: '8px' }}>لا توجد زيارات بعد</h3>
                <p style={{ color: 'var(--text-light)', fontSize: '14px' }}>
                  أكمل ملفك الشخصي وكن نشطاً لزيادة الزيارات
                </p>
              </div>
            ) : (
              viewers.map(v => renderUserCard(v, 'last_viewed_at', true))
            )}
          </div>
        )}

        {/* Likers List */}
        {activeTab === 'likers' && (
          <div className="card" style={{ padding: 0, overflow: 'hidden', borderRadius: '16px' }}>
            {likers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '50px 20px' }}>
                <div style={{ fontSize: '56px', marginBottom: '12px' }}>❤️</div>
                <h3 style={{ color: 'var(--text-dark)', marginBottom: '8px' }}>لا توجد إعجابات بعد</h3>
                <p style={{ color: 'var(--text-light)', fontSize: '14px' }}>
                  شارك ملفك وكن نشطاً لزيادة الإعجابات
                </p>
              </div>
            ) : (
              likers.map(l => renderUserCard(l, 'liked_at', true))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default StatsPage;
